import React, { FunctionComponent, memo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import { Avatar, Text } from '../Primitives';

import { IPeople } from '../../interface/People';
import { getRating } from '../../helpers/user';

import SubscriptionBadge from '../Premium/SubscriptionBadge';
import ProfileGenderIcon, { verifiedProfileGender } from '../Profile/ProfileGenderIcon';

type Props = {
  person: IPeople;
  isVertical?: boolean;
};

const PeopleCard: FunctionComponent<Props> = memo(({ person, isVertical }) => {
  const {
    firstName,
    lastName,
    picture,
    sportProfiles,
    pulseLevel,
    location,
    gender,
    status,
    username,
    subscriptions,
  } = person;
  const history = useHistory();

  // Fallback to uri or id if username is not available (Meilisearch data)
  const profileUri = username || (person as any).uri || person.id;
  const locationName = location?.name || location?.locality || location?.city;

  const firstFourSports = sportProfiles.slice(0, 4);

  const rating = getRating(firstFourSports[0]);

  const isShowBadge = firstFourSports.length < 2;
  const displayGender = verifiedProfileGender({
    gender,
    profileId: person.id,
    profileStatus: status,
  });

  const handleClick = useCallback(() => {
    history.push(`/${profileUri}`);
  }, [history, profileUri]);

  return (
    <div
      className="bg-white rounded-lg shadow-sm border border-border p-4 cursor-pointer hover:shadow-md transition-shadow"
      onClick={handleClick}
    >
      <div
        className={`flex items-center ${isVertical ? 'flex-col' : 'flex-row'}`}
      >
        <Avatar
          badgeText={isShowBadge ? rating : ''}
          size="lg"
          src={picture}
          pulseLevel={pulseLevel}
        />
        <div
          className={`flex-1 w-full ${isVertical ? 'mt-6 text-center' : 'ml-4 text-left'}`}
        >
          <div
            className={`flex flex-wrap mb-2 ${isVertical ? 'justify-center' : 'justify-start'}`}
          >
            {firstFourSports.map((sport) => {
              // Fallback: use sport.sport (uppercase key) if sportInfo.uri is not available
              const sportUri = sport.sportInfo?.uri || sport.sport?.toLowerCase();
              if (!sportUri) return null;
              return (
                <img
                  key={sport.id}
                  className="w-4 h-4 max-w-4 max-h-4 mr-2"
                  src={`https://rovo.fit/assets/sports/sport-${sportUri}-icon.png`}
                  alt={sport.sportInfo?.name || sport.sport || 'sport'}
                  loading="lazy"
                  decoding="async"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              );
            })}
          </div>
          <div className={`flex items-center gap-1 mb-2 ${isVertical ? 'justify-center' : ''}`}>
            <Text
              className="whitespace-nowrap overflow-hidden text-ellipsis"
              variant="lb.md"
            >{`${firstName} ${lastName}`}</Text>
            <SubscriptionBadge subscriptions={subscriptions} />
          </div>
          {(displayGender || locationName) && (
            <div className={`flex items-center ${isVertical ? 'justify-center' : ''}`}>
              <ProfileGenderIcon
                gender={displayGender}
                profileId={person.id}
                profileStatus={status}
              />
              {locationName && (
                <Text
                  className="whitespace-nowrap overflow-hidden text-ellipsis text-muted-foreground"
                  variant="pg.sm"
                >
                  {locationName}
                </Text>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

PeopleCard.displayName = 'PeopleCard';

export default PeopleCard;
